// Filet anti-perte du purchase GA4 (skylantern).
//
// Pourquoi ce module existe : le purchase GA4 doit partir une seule fois par transaction, et ne
// jamais être perdu si le client ne revient pas sur la page de confirmation.
//
// Le push est IMMÉDIAT, sans attendre le choix de consentement. En Consent Mode avancé le tag
// GA4 tire dans tous les cas (sans cookie si refusé), donc attendre ne change pas si
// l'événement part — seulement s'il porte un cookie. En revanche attendre fait perdre
// l'événement quand le visiteur quitte la page avant la fin de l'attente : mesuré le 16/09/2026
// sur la commande 614774, un `first_visit` (donc aucun choix stocké, donc l'attente pleine)
// arrivé sur la confirmation avec page_view mais sans purchase. Perdre l'attribution d'un achat
// coûte moins cher que perdre l'achat.
//
// CommonJS volontaire : testable avec Node nu (test/purchase-net.test.js), bundlé par
// webpack via `import purchaseNetFactory from './purchase-net'` dans plugins/app.js.
const PENDING_KEY = 'sky-pending-transaction'
const FIRED_PREFIX = 'sky-purchase-fired:'
const MAX_AGE_MS = 7 * 24 * 3600 * 1000

function readPending (storage) {
  const raw = storage.getItem(PENDING_KEY)
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw)
  } catch (error) {
    storage.removeItem(PENDING_KEY)
    return null
  }
}

module.exports = function purchaseNetFactory (app, options) {
  const storage = options.storage

  return {
    markPending (transactionId) {
      if (!transactionId) {
        return
      }
      storage.setItem(PENDING_KEY, JSON.stringify({ id: String(transactionId), at: Date.now() }))
    },
    clearPending () {
      storage.removeItem(PENDING_KEY)
    },
    hasFired (transactionId) {
      return !!storage.getItem(FIRED_PREFIX + String(transactionId))
    },
    // Pousse le purchase GA4 d'une transaction payée (orderId présent), une seule fois et sans
    // attendre. Le corps est synchrone — c'est ce qui garantit qu'un visiteur qui quitte la page
    // aussitôt a quand même poussé son achat ; le flag `fired`, posé dans le même tour de boucle,
    // suffit alors à écarter tout doublon, y compris entre deux appels concurrents.
    // Retourne une promesse : true si poussé (les appelants utilisent await / .catch).
    track (transaction) {
      const id = transaction && transaction.id ? String(transaction.id) : null
      if (!id || !transaction.properties || !transaction.properties.orderId || this.hasFired(id)) {
        return Promise.resolve(false)
      }
      app.$gtmEvents.onTransaction(transaction)
      storage.setItem(FIRED_PREFIX + id, String(Date.now()))
      const pending = readPending(storage)
      if (pending && pending.id === id) {
        this.clearPending()
      }
      return Promise.resolve(true)
    },
    // Rattrapage au boot : une transaction partie au paiement sans purchase envoyé.
    async recoverPending () {
      const pending = readPending(storage)
      if (!pending) {
        return
      }
      if (!pending.id || Date.now() - (pending.at || 0) > MAX_AGE_MS) {
        this.clearPending()
        return
      }
      if (this.hasFired(pending.id)) {
        this.clearPending()
        return
      }
      try {
        const { data: transaction } = await app.$api.get(`/transactions/${pending.id}`)
        await this.track(transaction)
      } catch (error) {
        // réseau/404 : nouvel essai à la prochaine visite, expiration à 7 j
      }
    }
  }
}
